<script lang="ts" setup>
import type { HeaderGradeTypeProps, HeaderViewProps } from '~/models/Subject'
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { ContentFolder } from '~/models/Content/ContentFolder'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { useRouter } from '#imports'
import { findRelevantGradeType } from '~/utils/gradeSorter'
import { setTitle } from '~/utils/dom'
import { waitFor } from '~/utils/asyncUtils'
import arrayUtils from '~/utils/arrayUtils'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import useAuthStore from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import { ContentType } from '~/models/Content/ContentType'
import { MaxTimeoutMs } from '~/constants/api'
import useSearchHelper from '~/composables/useSearchHelper'
import { useHeaderChildrenQuery } from '~/composables/useHeader'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import Header8To10 from '~/components/subject/gradetypes/Header8To10.vue'
import Header1To4 from '~/components/subject/gradetypes/Header1To4.vue'
import ContentFilteredAwayCallout from '~/components/ResourceFinder/ContentFilteredAwayCallout.vue'

const props = defineProps<HeaderViewProps>()

const router = useRouter()
const filterStore = useFilterStore()
const { gradeFilterFunc, removeTeacherArticlesFunc } = filterStore
const { selectedGrade } = storeToRefs(filterStore)

const { isPackage } = useContentHelper()
const { findContents } = useContentApi()
const { truthy } = arrayUtils()
const { setCurrentSubject } = useSubjectsStore()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { cardFields } = useSearchHelper()

const productStore = useProductStore()
const { getRelatedLocationsByGradeAndSubject, getProductFromResource } = productStore
const { isLoading: isLoadingProducts, hasLoaded: hasLoadedProducts } = storeToRefs(productStore)

const isUpcoming = ref(false)

const { data: header, isLoading } = useQuery({
  queryKey: computed(() => ['header-content', props.headerLocationId]),
  queryFn: async () => findContents<ContentProductHeader | ContentProductPackage | ContentFolder>({
    locationIdCriterion: [props.headerLocationId],
    contentTypeCriterion: [ContentType.ProductHeader, ContentType.ProductPackage, ContentType.Folder],
  }, 1).then(([header]) => header),
  staleTime: Infinity,
})

const product = computed(() => header?.value && getProductFromResource(header.value))

const { data: children, isLoading: isLoadingChildren } = useHeaderChildrenQuery(computed(() => header.value?.locationId), {
  sortField: computed(() => header.value?.sortField),
  sortOrder: computed(() => header.value?.sortOrder),
})

const { data: siblings } = useQuery({
  enabled: computed(() => !!(isAuthenticated.value && hasLoadedProducts.value && !!header?.value)),
  queryKey: computed(() => ['header-siblings', props.headerLocationId]),
  queryFn: () => findContents<ContentProductHeader | ContentProductPackage | ContentFolder>({
    parentLocationIdCriterion: [header.value!.parentLocationId],
    contentTypeCriterion: [ContentType.ProductHeader, ContentType.ProductPackage, ContentType.Folder],
    sortField: 'priority',
    sortOrder: 'asc',
  }),
  staleTime: Infinity,
})

const hasGradeFilter = computed(() => Boolean(product?.value
  && !!product.value.aunivers.gradeFilter
  && !!header?.value?.grades.length))

const filteredPackages = computed(() => (children?.value ?? [])
  .filter(child => isPackage(child) && (!hasGradeFilter.value || gradeFilterFunc(child))))
const filteredContents = computed(() => (children?.value ?? [])
  .filter(child => !isPackage(child) && (!hasGradeFilter.value || gradeFilterFunc(child)) && removeTeacherArticlesFunc(child)))
const filteredCombined = computed(() => [...filteredPackages.value, ...filteredContents.value])

const headerProps = computed((): HeaderGradeTypeProps => ({
  subjectCode: props.subjectCode,
  contents: filteredContents.value,
  packages: filteredPackages.value,
  siblings: siblings?.value ?? [],
  header: header?.value,
  subjectRoute: isUpcoming.value ? 'subject_upcoming' : 'subject',
  isLoading: isLoading.value,
  gradeFilter: hasGradeFilter.value,
  isLoadingProduct: isLoadingProducts.value,
}))

const gradeType = computed(() => {
  const grade = (header?.value?.grades ?? [])[0] ?? selectedGrade.value
  return grade ? findRelevantGradeType(grade) : null
})

watch(header, async () => {
  if (!header?.value)
    return
  setTitle(header.value.title)
  await waitFor(() => hasLoadedProducts.value, MaxTimeoutMs)
  if (!selectedGrade.value)
    return
  const { upcomingLocationIds } = await getRelatedLocationsByGradeAndSubject(selectedGrade.value, props.subjectCode)
  isUpcoming.value = upcomingLocationIds.filter(truthy).some(u => header.value.pathString.includes(`/${u}/`))
}, { immediate: true })

onBeforeUnmount(() => {
  setCurrentSubject(undefined)
})

watch(selectedGrade, () => {
  let path = router.resolve({
    name: 'header',
    params: {
      subject: props.subjectCode.toLowerCase(),
      locationId: props.headerLocationId,
    },
    query: {
      grade: selectedGrade.value
    }
  }).href
  if (router.currentRoute.value.params?.listId) {
    path = router.resolve({
      name: 'favorite_list_navigation',
      params: { listId: router.currentRoute.value.params.listId }
    }).href + path
  }

  router.replace(path)
})

watch(props, () => {
  if (!props.subjectCode)
    return
  setCurrentSubject(props.subjectCode)
}, { immediate: true })

onMounted(() => {
  if (!props.subjectCode)
    return
  setCurrentSubject(props.subjectCode)
})
</script>

<template>
  <div
    v-if="!filteredCombined.length && children && children.length > 0 && !isLoading && !isLoadingChildren"
    class="max-w-screen-au mx-auto flex flex-col gap-4 px-4 pt-36 pb-16 sm:px-8"
  >
    <ContentFilteredAwayCallout :contents="children" />
  </div>

  <div v-else-if="isLoading || isLoadingChildren">
    <AuSkeletonWrapper>
      <HeaderViewBannerGrid>
        <div class="grid-in-header-info py-24 md:py-32">
          <AuSkeleton height="4rem" />
          <AuSkeleton
            height="1.5rem"
            width="80%"
          />
          <AuSkeleton
            height="1.5rem"
            width="70%"
          />
          <AuSkeleton
            height="1.5rem"
            width="80%"
          />
          <AuSkeleton
            height="1.5rem"
            width="65%"
          />
          <AuSkeleton
            height="1.5rem"
            width="70%"
          />
        </div>
      </HeaderViewBannerGrid>
    </AuSkeletonWrapper>

    <!-- these sections are a copy of the skeleton in Header1To4 -->
    <section
      v-for="index in 2"
      :key="index"
    >
      <AuSkeletonWrapper class="max-w-screen-au mx-auto space-y-4 px-4 py-8 sm:px-8">
        <AuSkeleton
          height="36px"
          width="120px"
        />
        <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          <AuSkeleton
            v-for="i in 4"
            :key="i"
            height="280px"
          />
        </ul>
      </AuSkeletonWrapper>
    </section>
  </div>

  <div v-else>
    <Header1To4
      v-if="gradeType === GradeType.Grade1To4 && filteredCombined.length > 0"
      v-bind="headerProps"
    />

    <Header8To10
      v-else
      v-bind="headerProps"
    />
  </div>
</template>
